import React from 'react';
import { Navigate, Link as RouterLink } from 'react-router-dom';
import { Container, Paper, Box, useTheme, useMediaQuery, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { LoginForm } from '../components/LoginForm';
import { NewPasswordForm } from '../components/NewPasswordForm';
import { PasswordResetForm } from '../components/PasswordResetForm';
import { ConfirmPasswordResetForm } from '../components/ConfirmPasswordResetForm';
import {
  useIsAuthenticated,
  useSetNewPassword,
  usePasswordReset,
  usePasswordResetConfirm,
} from '../hooks/useAuth';
import { AuthResponseDto } from '../authentication.types';
import logo from '../../../../assets/logo.png';

type AuthState = 'login' | 'newPassword' | 'passwordReset' | 'confirmPasswordReset';

export const LoginPage: React.FC = () => {
  const [authState, setAuthState] = React.useState<AuthState>('login');

  const [passwordResetSuccess, setPasswordResetSuccess] = React.useState(false);
  const [resetEmail, setResetEmail] = React.useState<string>('');
  const [resetSuccess, setResetSuccess] = React.useState(false);
  const isAuthenticated = useIsAuthenticated();
  const setNewPasswordMutation = useSetNewPassword();
  const passwordResetMutation = usePasswordReset();
  const passwordResetConfirmMutation = usePasswordResetConfirm();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const switchToLogin = () => {
    setAuthState('login');
    setPasswordResetSuccess(false);
  };
  const switchToPasswordReset = () => {
    setAuthState('passwordReset');
    setPasswordResetSuccess(false);
  };

  const handleAuthResponse = (response: AuthResponseDto) => {
    if (response.isFirstLogin) {
      setAuthState('newPassword');
    }
    // If response.isSignedIn is true, the user will be redirected by the auth system
  };

  const handleNewPasswordSuccess = () => {
    // After successful password setup, user should be redirected automatically
    // by the authentication system, but we can reset state just in case
    setAuthState('login');
  };

  const handleNewPasswordCancel = () => {
    // Go back to login form
    setAuthState('login');
  };

  const handleNewPasswordSubmit = async (data: {
    firstName: string;
    lastName: string;
    newPassword: string;
  }) => {
    await setNewPasswordMutation.mutateAsync(data);
  };

  const handlePasswordResetSubmit = async (data: { email: string }) => {
    await passwordResetMutation.mutateAsync(data);
    setResetEmail(data.email);
    setPasswordResetSuccess(false);
    setAuthState('confirmPasswordReset');
  };

  const handlePasswordResetConfirm = async (data: {
    email: string;
    code: string;
    password: string;
  }) => {
    await passwordResetConfirmMutation.mutateAsync(data);
    setResetSuccess(true);
    setTimeout(() => {
      setResetSuccess(false);
      setAuthState('login');
    }, 3000);
  };

  const handleResendCode = async () => {
    if (resetEmail) {
      await passwordResetMutation.mutateAsync({ email: resetEmail });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isMobile
          ? theme.palette.background.default
          : `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
        py: isMobile ? 1 : 3,
        px: isMobile ? 2 : 3,
      }}
    >
      <Container
        component="main"
        maxWidth="sm"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          width: '100%',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          style={{ width: '100%' }}
        >
          {isMobile ? (
            // Mobile: Flat design without card elevation
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 2,
                px: 2,
              }}
            >
              {/* Mobile Branding Header */}
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mb: { xs: 2, sm: 3 },
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                >
                  <Box
                    component="img"
                    src={logo}
                    alt="Lecturo Logo"
                    sx={{
                      width: { xs: 40, sm: 48, md: 56 },
                      height: { xs: 40, sm: 48, md: 56 },
                      minWidth: 32,
                      minHeight: 32,
                      mb: { xs: 1, sm: 1.5 },
                      filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
                    }}
                  />
                </motion.div>
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                >
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      mb: { xs: 0.5, sm: 1 },
                      textAlign: 'center',
                      fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' },
                    }}
                  >
                    Lecturo
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      textAlign: 'center',
                      fontWeight: 400,
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    }}
                  >
                    Managing student feedback
                  </Typography>
                </motion.div>
              </Box>

              {/* Mobile Form Content */}
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 400,
                  flex: 1,
                  overflow: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <AnimatePresence mode="wait">
                  {authState === 'login' && (
                    <motion.div
                      key="login"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.2 }}
                    >
                      <LoginForm
                        onAuthResponse={handleAuthResponse}
                        onForgotPassword={switchToPasswordReset}
                      />
                    </motion.div>
                  )}
                  {authState === 'newPassword' && (
                    <motion.div
                      key="newPassword"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.2 }}
                    >
                      <NewPasswordForm
                        onSuccess={handleNewPasswordSuccess}
                        onCancel={handleNewPasswordCancel}
                        onSubmit={handleNewPasswordSubmit}
                        isLoading={setNewPasswordMutation.isPending}
                        error={setNewPasswordMutation.error?.message}
                      />
                    </motion.div>
                  )}
                  {authState === 'passwordReset' && (
                    <motion.div
                      key="passwordReset"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.2 }}
                    >
                      <PasswordResetForm
                        onSubmit={handlePasswordResetSubmit}
                        onCancel={switchToLogin}
                        isLoading={passwordResetMutation.isPending}
                        error={passwordResetMutation.error?.message}
                        success={passwordResetSuccess}
                      />
                    </motion.div>
                  )}
                  {authState === 'confirmPasswordReset' && resetEmail && (
                    <motion.div
                      key="confirmPasswordReset"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ConfirmPasswordResetForm
                        email={resetEmail}
                        onSubmit={handlePasswordResetConfirm}
                        onCancel={switchToLogin}
                        onResendCode={handleResendCode}
                        isLoading={passwordResetConfirmMutation.isPending}
                        error={passwordResetConfirmMutation.error?.message}
                        success={resetSuccess}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            </Box>
          ) : (
            // Desktop: Elevated card design with branding
            <Paper
              elevation={16}
              sx={{
                width: '100%',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                p: 6,
                borderRadius: 3,
                background: theme.palette.background.paper,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: `0 20px 40px rgba(0,0,0,0.1), 0 10px 20px rgba(0,0,0,0.05)`,
              }}
            >
              {/* Desktop Branding Header */}
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mb: { md: 2, lg: 3 },
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <Box
                    component="img"
                    src={logo}
                    alt="Lecturo Logo"
                    sx={{
                      width: { md: 40, lg: 48 },
                      height: { md: 40, lg: 48 },
                      minWidth: 32,
                      minHeight: 32,
                      mb: { md: 1, lg: 1.5 },
                      filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.1))',
                    }}
                  />
                </motion.div>
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      mb: { md: 0.5, lg: 1 },
                      textAlign: 'center',
                      fontSize: { md: '1.75rem', lg: '2rem' },
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    Lecturo
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{
                      textAlign: 'center',
                      fontWeight: 400,
                      mb: { md: 0.25, lg: 0.5 },
                      fontSize: { md: '0.9rem', lg: '1rem' },
                    }}
                  >
                    Managing student feedback
                  </Typography>
                  <Box
                    sx={{
                      width: 60,
                      height: 3,
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      borderRadius: 2,
                      mx: 'auto',
                    }}
                  />
                </motion.div>
              </Box>

              {/* Desktop Form Content */}
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 420,
                  flex: 1,
                  overflow: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <AnimatePresence mode="wait">
                  {authState === 'login' && (
                    <motion.div
                      key="login"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.2 }}
                    >
                      <LoginForm
                        onAuthResponse={handleAuthResponse}
                        onForgotPassword={switchToPasswordReset}
                      />
                    </motion.div>
                  )}
                  {authState === 'newPassword' && (
                    <motion.div
                      key="newPassword"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.2 }}
                    >
                      <NewPasswordForm
                        onSuccess={handleNewPasswordSuccess}
                        onCancel={handleNewPasswordCancel}
                        onSubmit={handleNewPasswordSubmit}
                        isLoading={setNewPasswordMutation.isPending}
                        error={setNewPasswordMutation.error?.message}
                      />
                    </motion.div>
                  )}
                  {authState === 'passwordReset' && (
                    <motion.div
                      key="passwordReset"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.2 }}
                    >
                      <PasswordResetForm
                        onSubmit={handlePasswordResetSubmit}
                        onCancel={switchToLogin}
                        isLoading={passwordResetMutation.isPending}
                        error={passwordResetMutation.error?.message}
                        success={passwordResetSuccess}
                      />
                    </motion.div>
                  )}
                  {authState === 'confirmPasswordReset' && resetEmail && (
                    <motion.div
                      key="confirmPasswordReset"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ConfirmPasswordResetForm
                        email={resetEmail}
                        onSubmit={handlePasswordResetConfirm}
                        onCancel={switchToLogin}
                        onResendCode={handleResendCode}
                        isLoading={passwordResetConfirmMutation.isPending}
                        error={passwordResetConfirmMutation.error?.message}
                        success={resetSuccess}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            </Paper>
          )}
        </motion.div>
      </Container>
    </Box>
  );
};
